/**
 * WorkOrderDetailPage
 * -------------------
 * Full-page admin view for a single work order. Wraps the entire lifecycle:
 *   - Header with status pill + completion % + finalize button
 *   - Updates feed (add / delete, internal vs external visibility)
 *   - Checklist (add / toggle / delete with auto progress bar)
 *   - Contractors (list + lead pill)
 *   - Contractor / Supplier Quotes (admin can edit · attach quote files · attach invoices)
 *   - Hospital sign-off status
 *
 * Route: `/admin/work-orders/:id`
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Trash2,
  Plus,
  Paperclip,
  Send,
  ShieldCheck,
  Eye,
  EyeOff,
  Loader2,
  Hammer,
  FileText,
  Receipt,
  Edit3,
  X,
  AlertTriangle,
  Sparkles,
  ExternalLink,
  Users,
  Mail,
  UserPlus,
  AlertOctagon,
  ClipboardCheck,
  MessageSquare,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  triage: "bg-slate-100 text-slate-700",
  assigned: "bg-blue-100 text-blue-700",
  in_progress: "bg-violet-100 text-violet-700",
  awaiting_hospital_signoff: "bg-amber-100 text-amber-800",
  awaiting_admin_review: "bg-indigo-100 text-indigo-700",
  disputed: "bg-rose-100 text-rose-700",
  closed: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-200 text-slate-600",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchWo = async () => {
    try {
      const res = await axios.get(`${API}/admin/work-orders/${id}`, { headers });
      // Endpoint returns { request: {...}, contractor_quotes: [...] }
      const body = res.data || {};
      const merged = {
        ...(body.request || body.work_order || body),
        contractor_quotes:
          body.contractor_quotes || body.request?.contractor_quotes || body.work_order?.contractor_quotes || [],
      };
      setWo(merged);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load work order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-10">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading work order…
      </div>
    );
  }
  if (!wo) {
    return (
      <div className="p-10 text-center text-slate-500">
        Work order not found.
        <Button onClick={() => navigate(-1)} variant="ghost" className="ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  const completionPct = (() => {
    const items = wo.checklist || [];
    if (!items.length) return 0;
    return Math.round((items.filter((i) => i.completed).length / items.length) * 100);
  })();
  const status = wo.status || wo.stage || "pending";

  return (
    <div className="space-y-5 max-w-5xl" data-testid="wo-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2 -ml-2 h-7">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Work Orders
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900">{wo.title || wo.description || wo.id}</h1>
            <Badge className={STATUS_COLORS[status] || "bg-slate-100 text-slate-700"}>
              {status.replace(/_/g, " ")}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1 font-mono">{wo.id}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Completion</div>
          <div className="text-2xl font-bold text-emerald-600">{completionPct}%</div>
          <div className="w-40 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${completionPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Quick info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <InfoCard label="Hospital">{wo.hospital_name || "—"}</InfoCard>
        <InfoCard label="Priority">{wo.priority || "—"}</InfoCard>
        <InfoCard label="Created">{fmt(wo.created_at)}</InfoCard>
      </div>

      {/* Assigned contractors */}
      <ContractorsCard wo={wo} />

      {/* Checklist */}
      <ChecklistCard
        wo={wo}
        token={token}
        onChange={fetchWo}
      />

      {/* Updates feed */}
      <UpdatesCard wo={wo} token={token} onChange={fetchWo} />

      {/* Hospital contacts — who gets notifications, quotes, sign-off etc */}
      <HospitalContactsCard wo={wo} token={token} onChange={fetchWo} />

      {/* Incidents — anything that happened during the WO that everyone needs to track */}
      <IncidentsCard wo={wo} token={token} onChange={fetchWo} />

      {/* Contractor / Supplier Quotes */}
      <QuotesCard wo={wo} token={token} onChange={fetchWo} />

      {/* Final approval + sign-off */}
      <SignOffCard wo={wo} token={token} onChange={fetchWo} />
    </div>
  );
}

// ----------------------------------------------------------------
// Sub-cards
// ----------------------------------------------------------------
function InfoCard({ label, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{children}</div>
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, children, action }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-500" />}
          <div>
            <h3 className="font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function ContractorsCard({ wo }) {
  const list = wo.assigned_contractors || [];
  if (list.length === 0 && !wo.assigned_contractor_name) {
    return (
      <Section title="Contractors" subtitle="No contractors assigned yet" icon={Hammer}>
        <p className="text-xs text-slate-500 italic">
          Use the &ldquo;Assign Contractor&rdquo; flow on the work orders board.
        </p>
      </Section>
    );
  }
  const display = list.length > 0 ? list : [{
    id: wo.assigned_contractor_id,
    name: wo.assigned_contractor_name,
    email: wo.assigned_contractor_email,
    is_lead: true,
  }];
  return (
    <Section title="Assigned Contractors" subtitle={`${display.length} assigned`} icon={Hammer}>
      <div className="space-y-2">
        {display.map((c, i) => (
          <div
            key={c.id || c.email || i}
            className="flex items-center justify-between rounded border border-slate-200 p-2.5 text-sm"
            data-testid={`wo-contractor-${i}`}
          >
            <div>
              <div className="font-semibold text-slate-900">{c.name || c.company_name || c.email}</div>
              <div className="text-xs text-slate-500">{c.email}</div>
            </div>
            {(c.is_lead || i === 0) && (
              <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">Lead</Badge>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function ChecklistCard({ wo, token, onChange }) {
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const items = wo.checklist || [];
  const headers = { Authorization: `Bearer ${token}` };

  const add = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/maintenance-requests/${wo.id}/checklist`,
        { label: newLabel },
        { headers }
      );
      setNewLabel("");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add task");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (task) => {
    try {
      await axios.patch(
        `${API}/admin/maintenance-requests/${wo.id}/checklist/${task.id}`,
        { completed: !task.completed },
        { headers }
      );
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (task) => {
    if (!window.confirm(`Remove "${task.label}"?`)) return;
    try {
      await axios.delete(
        `${API}/admin/maintenance-requests/${wo.id}/checklist/${task.id}`,
        { headers }
      );
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Section
      title="Progress Checklist"
      subtitle={`${items.filter((i) => i.completed).length}/${items.length} done`}
      icon={CheckCircle2}
    >
      {items.length === 0 && (
        <p className="text-xs text-slate-500 italic mb-2">
          Add tasks so contractors can check them off — the completion bar updates automatically.
        </p>
      )}
      <ul className="space-y-1.5">
        {items.map((task) => (
          <li
            key={task.id}
            className="flex items-center gap-2 rounded border border-slate-200 p-2 text-sm"
            data-testid={`wo-task-${task.id}`}
          >
            <button onClick={() => toggle(task)} className="flex-shrink-0">
              {task.completed ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <Circle className="w-5 h-5 text-slate-300" />
              )}
            </button>
            <span className={`flex-1 ${task.completed ? "line-through text-slate-400" : "text-slate-900"}`}>
              {task.label}
            </span>
            {task.completed && task.completed_by && (
              <span className="text-[10px] text-slate-400">{task.completed_by}</span>
            )}
            <button
              onClick={() => remove(task)}
              className="text-slate-300 hover:text-rose-500"
              title="Remove"
              data-testid={`wo-task-remove-${task.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-3">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a task (e.g. Replace RO membrane)"
          disabled={busy}
          data-testid="wo-task-input"
        />
        <Button
          onClick={add}
          disabled={busy || !newLabel.trim()}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="wo-task-add-btn"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </Section>
  );
}

function UpdatesCard({ wo, token, onChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [message, setMessage] = useState("");
  const [visibility, setVisibility] = useState("external");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const updates = wo.updates || [];

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setBusy(true);
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await axios.post(
          `${API}/admin/maintenance-requests/${wo.id}/updates/upload`,
          form,
          { headers: { ...headers, "Content-Type": "multipart/form-data" } }
        );
        setAttachments((p) => [...p, res.data]);
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setBusy(false);
  };

  const submit = async () => {
    if (!message.trim() && attachments.length === 0) {
      toast.error("Add a message or attachment");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/maintenance-requests/${wo.id}/updates`,
        {
          message: message || `Attached ${attachments.length} file(s)`,
          visibility,
          attachments,
          photos: attachments
            .filter((a) => /\.(png|jpe?g|webp|gif|heic)$/i.test(a.file_name || ""))
            .map((a) => a.file_url),
        },
        { headers }
      );
      setMessage("");
      setAttachments([]);
      toast.success(
        visibility === "external"
          ? "Update posted & emailed to hospital + contractor"
          : "Internal update posted"
      );
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post update");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm("Remove this update?")) return;
    try {
      await axios.delete(
        `${API}/admin/maintenance-requests/${wo.id}/updates/${u.id}`,
        { headers }
      );
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Section title="Updates & Activity" subtitle={`${updates.length} entries`} icon={Sparkles}>
      {/* Composer */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4 space-y-2" data-testid="wo-update-composer">
        <Textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Post a progress update, milestone, or note…"
          data-testid="wo-update-input"
        />
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a, i) => (
              <Badge
                key={i}
                variant="outline"
                className="gap-1 text-[11px] pr-1"
              >
                <Paperclip className="w-2.5 h-2.5" />
                <a href={`${API}${a.file_url}`} target="_blank" rel="noreferrer" className="hover:underline truncate max-w-[160px]">
                  {a.file_name}
                </a>
                <button onClick={() => setAttachments((p) => p.filter((_, x) => x !== i))} className="hover:text-rose-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 h-7 px-2 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
            <Paperclip className="w-3 h-3" /> Attach
            <input
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.txt,.mp4,.mov"
              className="hidden"
              onChange={handleFile}
              disabled={busy}
              data-testid="wo-update-attach-input"
            />
          </label>

          <div className="flex items-center gap-1 ml-auto text-xs">
            <button
              onClick={() => setVisibility("external")}
              className={`px-2 py-1 rounded ${
                visibility === "external"
                  ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="wo-update-visibility-external"
            >
              <Eye className="w-3 h-3 mr-1 inline" /> Hospital + Contractor
            </button>
            <button
              onClick={() => setVisibility("internal")}
              className={`px-2 py-1 rounded ${
                visibility === "internal"
                  ? "bg-rose-100 text-rose-700 border border-rose-200"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="wo-update-visibility-internal"
            >
              <EyeOff className="w-3 h-3 mr-1 inline" /> Internal only
            </button>
          </div>

          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="wo-update-post-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
            Post
          </Button>
        </div>
      </div>

      {/* Feed */}
      {updates.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No updates yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {[...updates].reverse().map((u) => (
            <li
              key={u.id}
              className={`rounded-lg border p-3 ${
                u.visibility === "internal" ? "border-rose-200 bg-rose-50/30" : "border-slate-200 bg-white"
              }`}
              data-testid={`wo-update-${u.id}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <span className="font-semibold text-slate-800">{u.by_name}</span>
                  <span className="text-slate-400">{fmt(u.created_at)}</span>
                  {u.visibility === "internal" ? (
                    <Badge className="bg-rose-100 text-rose-700 text-[10px] border-rose-200">
                      <EyeOff className="w-2.5 h-2.5 mr-1" /> Internal only
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border-emerald-200">
                      <Eye className="w-2.5 h-2.5 mr-1" /> Hospital + Contractor
                    </Badge>
                  )}
                  {u.update_type && u.update_type !== "progress" && (
                    <Badge variant="outline" className="text-[10px]">{u.update_type}</Badge>
                  )}
                </div>
                <button onClick={() => remove(u)} className="text-slate-300 hover:text-rose-500">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{u.message}</p>
              {Array.isArray(u.attachments) && u.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {u.attachments.map((a, i) => (
                    <a
                      key={i}
                      href={`${API}${a.file_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline border border-slate-200 rounded px-2 py-1 bg-white"
                    >
                      <Paperclip className="w-3 h-3" /> {a.file_name}
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function HospitalContactsCard({ wo, token, onChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", title: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/maintenance-requests/${wo.id}/hospital-contacts`,
        { headers },
      );
      setContacts(r.data.contacts || []);
    } catch (e) {
      // Silent — endpoint will 404 only if WO not found, already shown elsewhere
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wo.id]);

  const addContact = async () => {
    if (!form.email || !form.email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/maintenance-requests/${wo.id}/hospital-contacts`,
        {
          email: form.email.trim(),
          name: form.name.trim() || undefined,
          title: form.title.trim() || undefined,
          role: "additional",
          source: "manual",
        },
        { headers },
      );
      toast.success("Contact added — they'll be CC'd on quotes and updates");
      setForm({ email: "", name: "", title: "" });
      setAdding(false);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add contact");
    }
  };

  const removeContact = async (c) => {
    if (c.implicit) {
      toast.info("The submitter contact is captured from the WO itself — edit the WO to change it.");
      return;
    }
    if (!window.confirm(`Remove ${c.email} from this work order's contact list?`)) return;
    try {
      await axios.delete(
        `${API}/admin/maintenance-requests/${wo.id}/hospital-contacts/${c.id}`,
        { headers },
      );
      toast.success("Contact removed");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <Section
      title="Hospital Contacts"
      subtitle="Everyone here is auto-CC'd on quotes, updates, and the sign-off email."
      icon={Users}
      action={
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAdding((v) => !v)}
          className="h-7"
          data-testid="wo-hc-toggle-add"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1" />
          {adding ? "Cancel" : "Add contact"}
        </Button>
      }
    >
      {adding && (
        <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-3 mb-3 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="contact@hospital.com"
                data-testid="wo-hc-add-email"
              />
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Director of Facilities"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => { setAdding(false); setForm({ email: "", name: "", title: "" }); }}>
              Cancel
            </Button>
            <Button size="sm" onClick={addContact} className="bg-blue-600 hover:bg-blue-700" data-testid="wo-hc-add-submit">
              Add to work order
            </Button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-xs text-slate-500 italic">Loading contacts…</div>
      ) : contacts.length === 0 ? (
        <div className="text-xs text-slate-500 italic">
          No hospital contacts on file. Add at least one so we can send quotes and updates.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 -mt-1">
          {contacts.map((c) => (
            <li key={c.id} className="py-2 flex items-center gap-3" data-testid={`wo-hc-row-${c.id}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${c.role === "submitter" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                {(c.name || c.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 text-sm truncate">{c.name || c.email}</span>
                  {c.role === "submitter" && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px] uppercase tracking-wider">Submitter</Badge>
                  )}
                  {c.title && <span className="text-xs text-slate-500">· {c.title}</span>}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  <a href={`mailto:${c.email}`} className="hover:underline truncate">{c.email}</a>
                  {c.phone && <span className="ml-1">· {c.phone}</span>}
                </div>
              </div>
              {!c.implicit && (
                <button
                  onClick={() => removeContact(c)}
                  className="text-slate-300 hover:text-rose-500 p-1"
                  title="Remove"
                  data-testid={`wo-hc-remove-${c.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function IncidentsCard({ wo, token, onChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCount, setOpenCount] = useState(0);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    occurred_at: new Date().toISOString().slice(0, 16),
    notes: "",
    resolution_status: "ongoing",
    next_steps: "",
  });
  const [saving, setSaving] = useState(false);
  // Per-row status editor
  const [statusEditId, setStatusEditId] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: "ongoing",
    next_steps: "",
    resolution_notes: "",
  });
  const [savingStatus, setSavingStatus] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/work-orders/${wo.id}/incidents`, { headers });
      setItems(r.data.items || []);
      setOpenCount(r.data.open_count || 0);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wo.id]);

  const createIncident = async () => {
    if (!form.title.trim()) {
      toast.error("Add a title for the incident");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        occurred_at: form.occurred_at
          ? new Date(form.occurred_at).toISOString()
          : undefined,
        notes: form.notes.trim() || undefined,
        resolution_status: form.resolution_status || "ongoing",
        next_steps: form.next_steps.trim() || undefined,
      };
      await axios.post(`${API}/admin/work-orders/${wo.id}/incidents`, payload, { headers });
      toast.success("Incident logged — hospital + contractor notified");
      setForm({
        title: "",
        description: "",
        occurred_at: new Date().toISOString().slice(0, 16),
        notes: "",
        resolution_status: "ongoing",
        next_steps: "",
      });
      setAdding(false);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log incident");
    } finally {
      setSaving(false);
    }
  };

  const beginStatusEdit = (i) => {
    setStatusEditId(i.id);
    setStatusForm({
      status: i.resolution_status || "ongoing",
      next_steps: i.next_steps || "",
      resolution_notes: i.resolution_notes || "",
    });
  };

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      await axios.post(
        `${API}/admin/incidents/${statusEditId}/status`,
        {
          status: statusForm.status,
          by_name: "Admin",
          by_role: "admin",
          next_steps: statusForm.next_steps.trim() || undefined,
          resolution_notes: statusForm.resolution_notes.trim() || undefined,
        },
        { headers },
      );
      toast.success("Status updated");
      setStatusEditId(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this incident? The public link will stop working.")) return;
    try {
      await axios.delete(`${API}/admin/incidents/${id}`, { headers });
      toast.success("Incident removed");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Public link copied to clipboard"),
      () => toast.error("Copy failed — long-press the link instead"),
    );
  };

  return (
    <Section
      title={`Incidents${items.length > 0 ? ` · ${items.length}` : ""}`}
      subtitle="Problems or events during the WO. Each incident gets its own public link that's emailed to hospital + contractor."
      icon={AlertOctagon}
      action={
        <div className="flex items-center gap-2">
          {openCount > 0 && (
            <Badge className="bg-rose-100 text-rose-700">
              {openCount} open
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding((v) => !v)}
            className="h-7"
            data-testid="wo-incidents-toggle-add"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            {adding ? "Cancel" : "Log incident"}
          </Button>
        </div>
      }
    >
      {adding && (
        <div className="bg-rose-50/40 border border-rose-200 rounded-lg p-3 mb-3 space-y-2">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Asbestos found behind drywall"
              data-testid="wo-incidents-title"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date & time of incident</Label>
              <Input
                type="datetime-local"
                value={form.occurred_at}
                onChange={(e) => setForm({ ...form, occurred_at: e.target.value })}
                data-testid="wo-incidents-occurred"
              />
            </div>
            <div>
              <Label className="text-xs">Resolution status</Label>
              <select
                value={form.resolution_status}
                onChange={(e) => setForm({ ...form, resolution_status: e.target.value })}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="wo-incidents-status"
              >
                <option value="pending">Pending</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What happened? Who's affected?"
              data-testid="wo-incidents-description"
            />
          </div>
          <div>
            <Label className="text-xs">Next steps (optional)</Label>
            <Textarea
              rows={2}
              value={form.next_steps}
              onChange={(e) => setForm({ ...form, next_steps: e.target.value })}
              placeholder="What needs to happen next? Who's owning it?"
              data-testid="wo-incidents-next-steps"
            />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any extra context — vendor on site, parts requested, etc."
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={createIncident}
              disabled={saving}
              size="sm"
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="wo-incidents-submit"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Log incident & email parties
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-slate-500 italic">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 italic">
          No incidents on this work order. Hopefully it stays that way.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((i) => {
            const status = (i.resolution_status || "ongoing").toLowerCase();
            const isTerminal = status === "completed" || status === "failed";
            const STYLES = {
              pending: { box: "border-amber-200 bg-amber-50/60", badge: "bg-amber-500 text-white", label: "Pending" },
              ongoing: { box: "border-blue-200 bg-blue-50/40", badge: "bg-blue-600 text-white", label: "Ongoing" },
              completed: { box: "border-emerald-200 bg-emerald-50/60", badge: "bg-emerald-600 text-white", label: "Completed" },
              failed: { box: "border-rose-300 bg-rose-50/60", badge: "bg-rose-700 text-white", label: "Failed" },
            };
            const s = STYLES[status] || STYLES.ongoing;
            return (
              <li
                key={i.id}
                className={`rounded-lg border p-3 ${s.box}`}
                data-testid={`wo-incident-row-${i.id}`}
              >
                <div className="flex items-start gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${s.badge} text-[10px] uppercase tracking-wider`} data-testid={`wo-incident-status-${i.id}`}>
                        {s.label}
                      </Badge>
                      <span className="font-semibold text-slate-900 text-sm">{i.title}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Logged by {i.created_by_name || i.created_by_role} · {fmt(i.occurred_at)}
                      {isTerminal && i.resolved_at && (
                        <> · {status === "failed" ? "marked failed" : "resolved"} {fmt(i.resolved_at)} by {i.resolved_by_name}</>
                      )}
                    </p>
                    {i.description && (
                      <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{i.description}</p>
                    )}
                    {i.next_steps && (
                      <div className="mt-2 rounded border border-violet-200 bg-violet-50/60 px-2 py-1.5">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-violet-700">Next steps</div>
                        <p className="text-xs text-violet-900 whitespace-pre-wrap" data-testid={`wo-incident-next-steps-${i.id}`}>{i.next_steps}</p>
                      </div>
                    )}
                    {i.resolution_notes && (
                      <p className={`text-xs mt-1 whitespace-pre-wrap ${status === "failed" ? "text-rose-800" : "text-emerald-800"}`}>
                        <strong>{status === "failed" ? "Failure notes:" : "Resolution:"}</strong> {i.resolution_notes}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => beginStatusEdit(i)}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-300 bg-white text-xs hover:bg-slate-50"
                        data-testid={`wo-incident-change-status-${i.id}`}
                      >
                        <ClipboardCheck className="w-3 h-3" /> Change status
                      </button>
                      <a
                        href={i.public_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-300 bg-white text-xs hover:bg-slate-50"
                        data-testid={`wo-incident-open-${i.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Open public page
                      </a>
                      <button
                        onClick={() => copyLink(i.public_url)}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-300 bg-white text-xs hover:bg-slate-50"
                        data-testid={`wo-incident-copy-${i.id}`}
                      >
                        <Paperclip className="w-3 h-3" /> Copy link
                      </button>
                      {(i.comments?.length || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                          <MessageSquare className="w-3 h-3" /> {i.comments.length}
                        </span>
                      )}
                    </div>

                    {/* Inline status editor */}
                    {statusEditId === i.id && (
                      <div className="mt-3 border-t border-slate-200 pt-3 space-y-2 bg-white/80 rounded p-2" data-testid={`wo-incident-status-form-${i.id}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase font-bold text-slate-500">New status</Label>
                            <select
                              value={statusForm.status}
                              onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                              data-testid={`wo-incident-status-select-${i.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="ongoing">Ongoing</option>
                              <option value="completed">Completed</option>
                              <option value="failed">Failed</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-bold text-slate-500">Next steps</Label>
                          <Textarea
                            rows={2}
                            value={statusForm.next_steps}
                            onChange={(e) => setStatusForm({ ...statusForm, next_steps: e.target.value })}
                            placeholder="What needs to happen next?"
                            data-testid={`wo-incident-status-next-${i.id}`}
                          />
                        </div>
                        {(statusForm.status === "completed" || statusForm.status === "failed") && (
                          <div>
                            <Label className="text-[10px] uppercase font-bold text-slate-500">
                              {statusForm.status === "failed" ? "Failure notes" : "Resolution notes"}
                            </Label>
                            <Textarea
                              rows={2}
                              value={statusForm.resolution_notes}
                              onChange={(e) => setStatusForm({ ...statusForm, resolution_notes: e.target.value })}
                              placeholder={statusForm.status === "failed" ? "Why did it fail?" : "How was this resolved?"}
                              data-testid={`wo-incident-status-notes-${i.id}`}
                            />
                          </div>
                        )}
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => setStatusEditId(null)}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveStatus}
                            disabled={savingStatus}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid={`wo-incident-status-save-${i.id}`}
                          >
                            {savingStatus && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                            Update status
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => remove(i.id)}
                    className="text-slate-300 hover:text-rose-500 p-1"
                    title="Remove incident"
                    data-testid={`wo-incident-delete-${i.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}


function QuotesCard({ wo, token, onChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const quotes = wo.contractor_quotes || [];
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingId, setSavingId] = useState(null);

  const beginEdit = (q) => {
    setEditing(q.id);
    setEditForm({
      amount: q.amount || "",
      notes: q.notes || "",
      eta: q.eta || "",
      status: q.status || "pending",
      work_description: q.work_description || "",
      notify_contractor: true,
      override_reason: "",
    });
  };

  const saveEdit = async () => {
    setSavingId(editing);
    try {
      const res = await axios.patch(
        `${API}/admin/maintenance-requests/${wo.id}/contractor-quotes/${editing}`,
        {
          amount: editForm.amount ? Number(editForm.amount) : undefined,
          notes: editForm.notes || undefined,
          eta: editForm.eta || undefined,
          status: editForm.status || undefined,
          work_description: editForm.work_description || undefined,
          notify_contractor: !!editForm.notify_contractor,
          override_reason: editForm.override_reason || undefined,
        },
        { headers }
      );
      if (res.data?.changed_fields?.length > 0) {
        toast.success(
          res.data.contractor_notified
            ? `Quote updated — contractor notified (${res.data.changed_fields.length} field${res.data.changed_fields.length === 1 ? "" : "s"})`
            : "Quote updated"
        );
      } else {
        toast.success("Saved (no changes)");
      }
      setEditing(null);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingId(null);
    }
  };

  const uploadFile = async (quoteId, e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(
        `${API}/admin/maintenance-requests/${wo.id}/contractor-quotes/${quoteId}/attachments?kind=${kind}`,
        form,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${kind === "invoice" ? "Invoice" : "Quote"} file uploaded`);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const deleteFile = async (quoteId, attachmentId, kind) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await axios.delete(
        `${API}/admin/maintenance-requests/${wo.id}/contractor-quotes/${quoteId}/attachments/${attachmentId}?kind=${kind}`,
        { headers }
      );
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <Section
      title="Contractor / Supplier Quotes"
      subtitle="Internal — never visible to hospital"
      icon={FileText}
      action={
        <Badge variant="outline" className="text-rose-700 border-rose-300">
          <EyeOff className="w-3 h-3 mr-1" /> Admin only
        </Badge>
      }
    >
      {quotes.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No contractor quotes received yet.</p>
      ) : (
        <ul className="space-y-3">
          {quotes.map((q) => (
            <li key={q.id} className="rounded-lg border border-slate-200 p-3" data-testid={`wo-quote-${q.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                <div>
                  <div className="font-semibold text-slate-900">{q.contractor_name || q.supplier_name || "Quote"}</div>
                  <div className="text-xs text-slate-500">{q.contractor_email || q.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-slate-900">${Number(q.amount || 0).toFixed(2)}</div>
                  <Badge className={STATUS_COLORS[q.status] || "bg-slate-100 text-slate-700"}>
                    {q.status || "pending"}
                  </Badge>
                </div>
              </div>

              {editing === q.id ? (
                <div className="space-y-2 bg-slate-50 rounded p-2 border border-slate-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editForm.amount}
                        onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                        data-testid={`wo-quote-amount-${q.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">ETA</Label>
                      <Input
                        value={editForm.eta}
                        onChange={(e) => setEditForm({ ...editForm, eta: e.target.value })}
                        placeholder="e.g. 2026-06-15"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">
                      Work description <span className="text-emerald-700">· carried to Hospital Quote</span>
                    </Label>
                    <Textarea
                      rows={3}
                      value={editForm.work_description}
                      onChange={(e) => setEditForm({ ...editForm, work_description: e.target.value })}
                      placeholder="Describe the scope: what will be done, materials used, expected duration. This appears on the hospital quote."
                      data-testid={`wo-quote-workdesc-${q.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Internal notes</Label>
                    <Textarea
                      rows={2}
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      placeholder="Admin-only notes (not shown to hospital)"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">
                      Override reason <span className="text-slate-400">· optional, included in contractor email</span>
                    </Label>
                    <Input
                      value={editForm.override_reason}
                      onChange={(e) => setEditForm({ ...editForm, override_reason: e.target.value })}
                      placeholder="Why are you changing this?"
                      data-testid={`wo-quote-override-reason-${q.id}`}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!editForm.notify_contractor}
                      onChange={(e) => setEditForm({ ...editForm, notify_contractor: e.target.checked })}
                      className="w-3.5 h-3.5"
                      data-testid={`wo-quote-notify-${q.id}`}
                    />
                    <span className="text-slate-700">
                      Email contractor with the changes
                      {q.contractor_email && (
                        <span className="text-slate-400 ml-1">({q.contractor_email})</span>
                      )}
                    </span>
                  </label>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                    <Button onClick={saveEdit} disabled={savingId === q.id} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`wo-quote-save-${q.id}`}>
                      {savingId === q.id && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                      Save override
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {q.work_description && (
                    <div className="rounded border border-emerald-200 bg-emerald-50/60 px-2 py-1.5 mb-2">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Scope of work</div>
                      <p className="text-xs text-slate-800 mt-0.5 whitespace-pre-wrap" data-testid={`wo-quote-workdesc-view-${q.id}`}>
                        {q.work_description}
                      </p>
                    </div>
                  )}
                  {q.notes && <p className="text-xs text-slate-600 mb-2">{q.notes}</p>}
                  <div className="flex gap-1.5 flex-wrap items-center text-xs">
                    <Button size="sm" variant="outline" onClick={() => beginEdit(q)} className="h-7 px-2" data-testid={`wo-quote-edit-${q.id}`}>
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <label className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
                      <Paperclip className="w-3 h-3" /> Attach Quote File
                      <input type="file" className="hidden" onChange={(e) => uploadFile(q.id, e, "quote")} data-testid={`wo-quote-attach-${q.id}`} />
                    </label>
                    <label className="inline-flex items-center gap-1 h-7 px-2 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 cursor-pointer">
                      <Receipt className="w-3 h-3" /> Attach Invoice
                      <input type="file" className="hidden" onChange={(e) => uploadFile(q.id, e, "invoice")} data-testid={`wo-invoice-attach-${q.id}`} />
                    </label>
                  </div>
                  {(q.attachments?.length || q.invoices?.length) > 0 && (
                    <div className="mt-2 space-y-1">
                      {(q.attachments || []).map((a) => (
                        <FileChip key={a.id} file={a} kind="quote" onDelete={() => deleteFile(q.id, a.id, "quote")} />
                      ))}
                      {(q.invoices || []).map((a) => (
                        <FileChip key={a.id} file={a} kind="invoice" onDelete={() => deleteFile(q.id, a.id, "invoice")} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function FileChip({ file, kind, onDelete }) {
  const isInvoice = kind === "invoice";
  return (
    <div className={`flex items-center gap-2 rounded border p-1.5 text-xs ${isInvoice ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
      {isInvoice ? <Receipt className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" /> : <Paperclip className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />}
      <a
        href={`${API}${file.file_url}`}
        target="_blank"
        rel="noreferrer"
        className="flex-1 truncate hover:text-blue-600 hover:underline"
      >
        {file.file_name}
      </a>
      <span className="text-[10px] text-slate-400">{Math.round((file.size_bytes || 0) / 1024)} KB</span>
      <button onClick={onDelete} className="text-slate-300 hover:text-rose-500" title="Remove">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function SignOffCard({ wo, token, onChange }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [openFinalize, setOpenFinalize] = useState(false);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const status = wo.status || wo.stage;

  const finalize = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/maintenance-requests/${wo.id}/finalize`,
        { summary },
        { headers }
      );
      toast.success("Finalized — hospital has been emailed the sign-off link");
      // Copy link to clipboard for convenience
      navigator.clipboard?.writeText(res.data.signoff_url).catch(() => {});
      setOpenFinalize(false);
      setSummary("");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to finalize");
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    const reason = window.prompt("Reason for reopening?", "");
    if (reason === null) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/maintenance-requests/${wo.id}/reopen`,
        { reason },
        { headers }
      );
      toast.success("Reopened");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reopen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title="Final Approval & Hospital Sign-off"
      subtitle="Admin must finalize before the hospital can sign off"
      icon={ShieldCheck}
      action={
        <div className="flex gap-2">
          {status === "closed" || status === "disputed" ? (
            <Button variant="outline" onClick={reopen} disabled={busy}>
              Reopen
            </Button>
          ) : (
            <Button
              onClick={() => setOpenFinalize(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wo-finalize-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-1" /> Finalize & send to hospital
            </Button>
          )}
        </div>
      }
    >
      {status === "awaiting_hospital_signoff" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4" /> Awaiting hospital sign-off</div>
          <p className="text-xs mt-1">Finalized {fmt(wo.final_approved_at)} by {wo.final_approved_by}. Hospital was emailed the sign-off link.</p>
          {wo.signoff_token && (
            <a
              href={`/work-orders/sign-off/${wo.signoff_token}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-700 hover:underline mt-1 inline-flex items-center gap-1"
            >
              Open public sign-off link <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
      {status === "closed" && wo.signoff_decision === "approve" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" /> Signed off & closed</div>
          <p className="text-xs mt-1">Signed by <b>{wo.signoff_decided_by}</b> ({wo.signoff_signed_title || "Hospital"}) on {fmt(wo.signoff_decided_at)}.</p>
        </div>
      )}
      {status === "disputed" && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4" /> Hospital disputed the work</div>
          <p className="text-xs mt-1">Disputed by <b>{wo.signoff_decided_by}</b> on {fmt(wo.signoff_decided_at)}.</p>
          {wo.signoff_feedback && <p className="text-xs mt-2 italic">&ldquo;{wo.signoff_feedback}&rdquo;</p>}
        </div>
      )}
      {!status?.includes("signoff") && status !== "closed" && status !== "disputed" && (
        <p className="text-xs text-slate-500 italic">
          When the work is complete, click &ldquo;Finalize&rdquo; — the hospital will get an email with a sign-off link. They never see contractor / supplier quotes.
        </p>
      )}

      {/* Finalize dialog */}
      <Dialog open={openFinalize} onOpenChange={setOpenFinalize}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ShieldCheck className="w-5 h-5" /> Finalize & send to hospital
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This locks the work order, generates a one-time sign-off link, and emails the hospital with a button to approve or dispute the work. The hospital sees only the customer-safe summary &mdash; never supplier quotes or invoices.
            </p>
            <Label className="text-xs">Customer-facing summary (optional)</Label>
            <Textarea
              rows={4}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="e.g. Replaced 2 RO membranes, sanitized loop. Conductivity tested at 2µS/cm — passing AAMI ST108."
              data-testid="wo-finalize-summary"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenFinalize(false)} disabled={busy}>Cancel</Button>
            <Button onClick={finalize} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wo-finalize-confirm-btn">
              {busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Finalize & email hospital
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
